/**
 * List of products which was updated and become out of stock
 *
 * @attr {String} [quantity-label] - Quantity label
 * @attr {String} [quantity-label-short] - Quantity label short version
 * @attr {String} [css-class] - Additional css class for product item
 * @attr {String} [items-available-label] - Available amount of items label
 * @attr {Boolean} [use-adjusted-cart-entries] - Use adjusted cart array items as product entries
 *
 * @param {Array} entries - Product entries
 *
 * HTML Structure Example:
 * <e2-out-of-stock-products></e2-out-of-stock-products>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/cart';
import { selectors as globalSelectors } from '@elab/core/store/global';

export class OutOfStockProducts extends LightLitElement {
    static get properties() {
        return {
            entries: { type: Array, attribute: false },
            cssClass: { type: String, attribute: 'css-class' },
            quantityLabel: { type: String, attribute: 'quantity-label' },
            selectMethod: { type: String, attribute: 'select-method' },
            enabledFeatures: { type: Array, attribute: 'enabled-features' },
            showOutOfStockQuantity: { type: Boolean, attribute: 'show-out-of-stock-quantity' },
            itemsAvailableLabel: {
                type: String,
                attribute: 'items-available-label',
            },
            quantityLabelShort: {
                type: String,
                attribute: 'quantity-label-short',
            },
            hasForbiddenEntries: { type: Boolean, attribute: false },
            productAvailableLabel: { type: String, attribute: 'product-available-label' },
            productUnavailableLabel: { type: String, attribute: 'product-unavailable-label' },
        };
    }

    constructor() {
        super();

        this.entries = [];
        this.enabledFeatures = [];
        this.cssClass = '';
        this.quantityLabel = '';
        this.itemsAvailableLabel = '';
        this.quantityLabelShort = '';
        this.hasForbiddenEntries = false;
        this.showOutOfStockQuantity = false;
    }

    featureEnabled(featureName) {
        return this.enabledFeatures.find((feature = '') => feature.trim() === featureName);
    }

    getItem(name, content) {
        return html`
            <div class="product__${name}">${content}</div>
        `;
    }

    getVariantsClassName(isInline) {
        return classMap({
            product__variants: true,
            'product__variants--inline': isInline,
        });
    }

    getProductVariants(variants, isInline) {
        return html`
            <e2-product-variants
                class="${this.getVariantsClassName(isInline)}"
                .variants="${variants}"
                variant-class="product-variant--out-of-stock${isInline ? '-inline' : ''}"
                ?is-text-color="${isInline}"
            ></e2-product-variants>
        `;
    }

    updated(changedProperties) {
        if (changedProperties.has('entries')) {
            this.dispatchEvent(new CustomEvent('adjust-content-data'));
        }
    }

    getProductTemplate = ({ product = {}, quantity = 0, quantityRemoved = 0, stockData }) => {
        const { stock = {}, name = '', price = {}, variantValueCategories = [] } = product;
        const stockLevel = stockData ? stockData.stockLevel : stock.stockLevel;

        return html`
            <li class="product ${this.cssClass}">
                ${this.getItem(
                    'quantity-to-remove',
                    html`
                        <span class="product__quantity-label">
                            ${this.quantityLabel}
                        </span>
                        <span class="product__quantity-label product__quantity-label--mob">
                            ${this.quantityLabelShort}
                        </span>
                        <span class="product__quantity-value">
                            ${quantity - (stockLevel || 0)}
                        </span>
                    `,
                )}
                ${this.getItem('name', name)}

                <e2-product-info
                    class="product__info"
                    .product="${product}"
                    css-class="product-info--out-of-stock"
                >
                    <span v-slot="after-name">
                        ${this.getProductVariants(variantValueCategories, true)}
                    </span>
                </e2-product-info>

                ${this.getProductVariants(variantValueCategories)}
                ${this.getItem(
                    'quantity',
                    html`
                        <span class="product__quantity-label">
                            ${this.quantityLabel}
                        </span>
                        <span class="product__quantity-label product__quantity-label--mob">
                            ${this.quantityLabelShort}
                        </span>
                        <span class="product__quantity-value">
                            ${quantity}
                        </span>
                    `,
                )}
                ${quantityRemoved > 0
                    ? this.getItem(
                          'quantityRemoved',
                          html`
                              <span class="product__quantity-label">
                                  ${this.quantityLabel}
                              </span>
                              <span class="product__quantity-label product__quantity-label--mob">
                                  ${this.quantityLabelShort}
                              </span>
                              <span class="product__quantity-value">
                                  ${quantityRemoved}
                              </span>
                          `,
                      )
                    : null}
                ${this.getItem('price', price.formattedValue || '')}
                ${this.featureEnabled('new-quantity')
                    ? this.getItem(
                          'new-quantity',
                          html`
                              <p class="product__new-quantity--value">
                                  ${`${this.itemsAvailableLabel}: ${quantity}`}
                              </p>
                          `,
                      )
                    : null}
                ${this.featureEnabled('price-of-unavailable-product')
                    ? this.getItem('price-of-unavailable-product', price.formattedValue || '')
                    : null}
            </li>
        `;
    };

    productImageTemplate(thumbnailImage, productName) {
        return html`
            <e2-product-image
                .images="${thumbnailImage}"
                .productName="${productName}"
                format="list"
                classes="product-image"
            >
            </e2-product-image>
        `;
    }

    getForbiddenProductTemplate = ({ product = {} }) => {
        return html`
            <li class="product forbidden-product">
                <div class="forbidden-product__image">
                    <e2-link
                        class="forbidden-product__image-wrapper"
                        href="${product.url}"
                        v-slot="product-image"
                    >
                        ${this.productImageTemplate(product.images, product.name)}
                    </e2-link>
                </div>
                <div class="forbidden-product__info">
                    <div class="forbidden-product__info--name">
                        <strong>${product.name}</strong>
                    </div>
                    <div
                        class="forbidden-product__info--status ${product.isForbidden
                            ? 'forbidden-product__info--highlight'
                            : null}"
                    >
                        ${product.isForbidden
                            ? this.productUnavailableLabel
                            : this.productAvailableLabel}
                    </div>
                </div>
            </li>
        `;
    };

    render() {
        const productTemplate = this.hasForbiddenEntries
            ? this.getForbiddenProductTemplate
            : this.getProductTemplate;
        return html`
            <ul class="products-list">
                ${this.entries.map(productTemplate)}
            </ul>
        `;
    }
}

const mapStateToProps = (state, ctx) => {
    const hasForbiddenEntriesInAdjustedCart = selectors.hasForbiddenEntriesInAdjustedCart(state);
    const deliveryCountry = globalSelectors.getDeliveryCountry(state);

    const entries = ctx.selectMethod
        ? selectors[ctx.selectMethod](state) || []
        : hasForbiddenEntriesInAdjustedCart
        ? selectors.getEntries(state)
        : selectors.getOutOfStockEntries(state);

    const productEntriesDTO = entries =>
        entries.map(({ quantity, quantityAdded = 0, product, entry, ...rest }) => {
            if (!product && entry) {
                product = entry.product;
            }
            const outOfStockQuantity =
                rest.stockData && Math.abs(quantity - rest.stockData.stockLevel);
            const quantityRemoved =
                quantityAdded < 0
                    ? Math.abs(quantityAdded)
                    : ctx.showOutOfStockQuantity
                    ? outOfStockQuantity
                    : quantity;

            return { quantity, quantityRemoved, product, ...rest };
        });

    const getForbiddenProductsEntriesDTO = (entries, country) => {
        return entries.map(({ product, ...rest }) => {
            const { forbiddenCountries } = product;
            const index = forbiddenCountries.findIndex(({ isocode }) => isocode === country);
            product.isForbidden = false;
            if (index > -1) {
                product.isForbidden = true;
            }
            return { product, ...rest };
        });
    };

    return {
        entries: ctx.selectMethod
            ? productEntriesDTO(entries)
            : hasForbiddenEntriesInAdjustedCart
            ? getForbiddenProductsEntriesDTO(entries, deliveryCountry)
            : entries,
        hasForbiddenEntries: hasForbiddenEntriesInAdjustedCart,
    };
};

export default () =>
    customElements.define('e2-out-of-stock-products', connect(mapStateToProps)(OutOfStockProducts));
